import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "../../lib/motion";

const HERO_IMG = "/hero-okome.jpg";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1.9 + i * 0.12 },
  }),
};

const Line = ({ i, children, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.7]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Authentic Japanese catering platter"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-charcoal"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/30" />

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="label-caps text-gold mb-6"
        >
          Handmade Japanese Catering — Auckland
        </motion.span>

        <h1 className="font-serif text-cream leading-[0.95] text-5xl md:text-7xl lg:text-[5.8rem] max-w-5xl">
          <Line i={0}>Authentic Japanese</Line>
          <Line i={1}>
            Catering for <span className="italic text-gold">Every</span>
          </Line>
          <Line i={2}>Occasion</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.9 }}
          className="mt-8 max-w-xl text-cream/85 text-base md:text-lg leading-relaxed"
        >
          From office lunches to private celebrations, OKOME Catering delivers
          freshly prepared Japanese meals made with care.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.7, duration: 0.9 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-order-btn"
            onClick={() => scrollToId("contact")}
            className="rounded-full bg-gold px-8 py-3.5 text-charcoal font-medium hover:bg-cream transition-colors duration-300"
          >
            Order Now
          </button>
          <button
            data-testid="hero-menu-btn"
            onClick={() => scrollToId("menu")}
            className="rounded-full border border-cream/50 px-8 py-3.5 text-cream hover:bg-cream hover:text-charcoal transition-colors duration-300"
          >
            View Menu
          </button>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToId("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 text-cream/70 hover:text-gold transition-colors"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="inline-block"
        >
          <ArrowDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
